import { Entity, UniqueEntityId } from "@kernel/ddd";
import { WebRtcPeerService } from "./services";
import { UserProps } from "modules/room/services";

export interface PeerProps {
  config: RTCConfiguration;
  polite: boolean;
  user: UserProps;
}

export class Peer extends Entity<PeerProps> {
  private service: WebRtcPeerService;
  public dataChannel: RTCDataChannel | null;
  protected constructor(props: PeerProps, id?: UniqueEntityId) {
    super(props, id);
    this.service = new WebRtcPeerService(this);
    this.dataChannel = null;
  }

  public get id(): UniqueEntityId {
    return this._id;
  }

  public get config(): RTCConfiguration {
    return this.props.config;
  }

  public get polite(): boolean {
    return this.props.polite;
  }

  public set polite(polite: boolean) {
    this.props.polite = polite;
  }

  public get user(): UserProps {
    return this.props.user;
  }

  public set user(user: UserProps) {
    this.props.user = user;
  }

  public get userRemoteId(): number {
    return this.props.user.id;
  }

  public getRtcService(): WebRtcPeerService {
    return this.service;
  }

  public close() {
    this.service.close();
  }

  public getTransceivers(): RTCRtpTransceiver[] {
    return this.service.getTransceivers();
  }

  public addTrack(track: MediaStreamTrack): RTCRtpSender {
    return this.service.addTrack(track);
  }

  public removeTrack(): void {
    this.service.removeTrack();
  }

  public async replaceTrack(stream: MediaStream): Promise<void> {
    await this.service.replaceTrack(stream);
  }

  public addTransceiver(
    trackOrKind: string | MediaStreamTrack,
    init: RTCRtpTransceiverInit
  ): RTCRtpTransceiver {
    return this.service.addTransceiver(trackOrKind, init);
  }

  public createDataChannel(label: string): RTCDataChannel | null {
    this.dataChannel = this.service.createDataChannel(label);
    return this.dataChannel;
  }

  public async getStats(): Promise<RTCStatsReport> {
    return await this.service.getStats();
  }

  public async handleDescription(
    description: RTCSessionDescription,
    from: number
  ) {
    this.service.handleDescription(description, from);
  }

  public async handleCandidate(candidate: RTCIceCandidate) {
    this.service.handleCandidate(candidate);
  }

  public clone(): Peer {
    return new Peer({ ...this.props });
  }

  public toString(): string {
    return JSON.stringify(this.props);
  }

  public static create(props: PeerProps, id?: UniqueEntityId): Peer {
    return new Peer(props, id);
  }
}
